import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiBell, FiMessageSquare, FiCheckSquare, FiMenu, FiX } from 'react-icons/fi';
import Button from '../components/ui/Button';
import Avatar from '../components/Avatar';
import logoImg from '../assets/logo.png';

/**
 * Premium frosted glass header with minimal navigation.
 */
export default function Header({
  isLoggedIn, username, avatar, isPremium, isAdmin, isCoach, coachId,
  hasUnread, onAuthModalOpen,
  onNotificationClick, onMessagesClick, onTaskPanelClick,
  onAvatarClick, dropdownOpen, dropdownRef,
  xpProgress, xp, xpPerLevel, level,
  onLogout, onNavigate,
  children, // Dropdown/panel content rendered from parent
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef(null);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const navItems = [
    { label: 'Ana Sayfa', path: '/' },
    { label: 'Çalışma', path: '/study-time' },
    { label: 'Analiz', path: '/trial' },
    { label: 'Forum', path: '/forum' },
    { label: 'Hedefler', path: '/goals' },
    { label: 'Liderlik', path: '/leaderboard' },
    // Coach-specific nav item
    ...(isCoach ? [{ label: '🎓 Koç Paneli', path: '/coach' }] : [{ label: '🌟 Koçluk', path: '/my-program' }]),
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <motion.header
      className="header frosted"
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* Logo */}
      <div className="header-left" onClick={() => navigate('/')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <img src={logoImg} alt="Yelken Logo" className="header-logo-img" />
        <span className="logo-text">YELKEN</span>
      </div>

      {/* Nav */}
      <nav className="header-center">
        <button
          className="hamburger-btn"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menü"
        >
          {menuOpen ? <FiX size={20} /> : <FiMenu size={20} />}
        </button>

        <div className={`nav-pills ${menuOpen ? 'nav-pills--open' : ''}`} ref={navRef}>
          {navItems.map((item) => (
            <button
              key={item.path}
              className={`nav-pill ${isActive(item.path) ? 'nav-pill--active' : ''}`}
              onClick={() => {
                navigate(item.path);
                setMenuOpen(false);
              }}
            >
              {item.label}
            </button>
          ))}
          {isAdmin && (
            <button
              className={`nav-pill ${isActive('/admin') ? 'nav-pill--active' : ''}`}
              onClick={() => { navigate('/admin'); setMenuOpen(false); }}
            >
              Admin
            </button>
          )}
        </div>
      </nav>

      {/* Right */}
      <div className="header-right">
        {isLoggedIn ? (
          <div className="header-icons">
            <button className="header-icon-btn" onClick={onTaskPanelClick} aria-label="Görevler">
              <FiCheckSquare size={18} />
            </button>
            <button className="header-icon-btn" onClick={onNotificationClick} aria-label="Bildirimler">
              <FiBell size={18} />
              {hasUnread && <span className="header-icon-dot" />}
            </button>
            {/* <button className="header-icon-btn" onClick={onMessagesClick} aria-label="Mesajlar">
              <FiMessageSquare size={18} />
            </button> */}

            <div className="header-avatar-wrap" onClick={onAvatarClick}>
              <span className="header-username">@{username}</span>
              <div className="header-avatar" style={{width: '32px', height: '32px'}}>
                <Avatar avatarData={avatar} seedFallback={username} size={32} />
              </div>
            </div>
          </div>
        ) : (
          <Button variant="primary" size="sm" onClick={onAuthModalOpen}>
            Giriş Yap / Kayıt Ol
          </Button>
        )}
      </div>

      {/* Panels/dropdowns rendered as children */}
      {children}
    </motion.header>
  );
}
